// src/components/player/tiles/Tile3ZScoreSkillPitcher.js
// Tile 3: Skill Metrics Z-Score Analysis (PITCHER VERSION)
// Displays pitcher's Z-scores and percentiles for skill metrics vs MLB/league population
// BB/9, HR/9, FIP are INVERTED (lower values show as better Z-scores)

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { Settings } from 'lucide-react';

const Tile3ZScoreSkillPitcher = ({ data, loading, error }) => {
  // Tile wrapper component
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Format stat name for display
  const formatStatName = (fieldName) => {
    const labels = {
      'k_per_9': 'K/9',
      'bb_per_9': 'BB/9',
      'k_bb_ratio': 'K/BB',
      'hr_per_9': 'HR/9',
      'gb_pct': 'GB%',
      'fip': 'FIP'
    };
    return labels[fieldName] || fieldName.toUpperCase();
  };

  // Format raw stat value
  const formatRawValue = (fieldName, value) => {
    if (value === null || value === undefined) return '-';
    
    // GB% shows as percentage
    if (fieldName === 'gb_pct') {
      return value.toFixed(1) + '%';
    }
    
    // All other stats show 2 decimals
    return value.toFixed(2);
  };

  // Get Z-score indicator emoji
  const getZScoreIndicator = (zScore) => {
    if (zScore >= 2.0) return '🚀';
    if (zScore >= 1.0) return '⭐';
    if (zScore >= 0.5) return '↑';
    if (zScore >= -0.5) return '→';
    return '↓';
  };

  // Get Z-score color class
  const getZScoreColor = (zScore) => {
    if (zScore >= 2.0) return dynastyTheme.classes.text.success;
    if (zScore >= 1.0) return dynastyTheme.classes.text.primary;
    if (zScore >= -0.5) return dynastyTheme.classes.text.white;
    return dynastyTheme.classes.text.error;
  };

  // Check data structure — V13: canonical cache record under data.mlb
  const mlb = data?.mlb;
  const rawStats = mlb?.stats_snapshot?.raw_stats;
  const hasPlayerData = !!mlb;
  const hasRawStats = !!rawStats;
  const hasZScores = !!mlb?.category_z_scores;
  const hasPercentiles = !!mlb?.percentiles;
  const role = mlb?.position_code === 'RP' ? 'reliever' : 'pitcher';

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <Settings className="w-4 h-4 mr-2" />
          ⚙️ Skill Metrics
        </h4>
        
        {loading ? (
          <div className={`flex-1 flex items-center justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading analytics...
            </div>
          </div>
        ) : error ? (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.error} mb-2`}>
              {error}
            </div>
          </div>
        ) : hasPlayerData && hasRawStats && hasZScores ? (
          <div className="flex-1 flex flex-col">
            {/* Warning about inverted stats */}
            <div className={`text-xs ${dynastyTheme.classes.text.neutral} mb-2 opacity-75`}>
              ⚠️ BB/9, HR/9, FIP: lower is better
            </div>

            {/* Z-Score Grid */}
            <div className="flex-1 overflow-y-auto space-y-1">
              {Object.keys(rawStats).map((stat) => {
                const rawValue = rawStats[stat];
                const zScore = mlb.category_z_scores[stat];
                const percentile = hasPercentiles ? mlb.percentiles[stat] : null;

                return (
                  <div key={stat} className="grid grid-cols-4 gap-2 text-sm items-center py-1">
                    {/* Stat Name & Value */}
                    <div className={`col-span-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      <div>{formatStatName(stat)}</div>
                      <div className="text-xs opacity-75">{formatRawValue(stat, rawValue)}</div>
                    </div>

                    {/* Z-Score */}
                    <div className={`col-span-2 text-center`}>
                      <span className={`text-sm ${getZScoreColor(zScore)}`}>
                        {zScore >= 0 ? '+' : ''}{zScore.toFixed(2)} {getZScoreIndicator(zScore)}
                      </span>
                    </div>

                    {/* Percentile */}
                    <div className={`col-span-1 text-right ${dynastyTheme.classes.text.neutral}`}>
                      {percentile ? `${percentile}th` : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Footer with MLB population info */}
            {mlb?.total_players && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700`}>
                <div>MLB Population: {mlb.total_players} {role}s</div>
                {data?.league?.total_players && (
                  <div>League: {data.league.total_players} {role}s</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              No skill metrics data available
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile3ZScoreSkillPitcher;
